import {
  ArrowBack as ArrowBackIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import {
  FileDropzone,
  WizardProgress,
} from '../../components/onboarding/bulk-import';
import TerminationReviewTable from '../../components/termination/TerminationReviewTable';
import TerminationSubmitStep from '../../components/termination/TerminationSubmitStep';
import { useTerminationImport } from '../../hooks/useTerminationImport';
import { WIZARD_STEPS } from '../../utils/constants';
import { paths } from '../../utils/paths';

const BulkTerminationPage: React.FC = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [csvData, setCsvData] = useState<TerminationRow[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [submitResults, setSubmitResults] = useState<
    BulkTerminationRowResult[] | null
  >(null);

  const {
    handleDownloadExcelTemplate,
    handleFileSelect,
    handleValidate,
    handleExecute,
  } = useTerminationImport({
    onUploadSuccess: (rows) => {
      setCsvData(rows);
      setIsValidated(false);
      setActiveStep(1);
    },
    onUploadError: (error) => setUploadError(error),
    setCsvData,
    setIsUploading,
    setIsValidating,
    setIsValidated,
    setIsDownloadingTemplate,
    setIsSubmitting,
    setSubmitProgress,
    setSubmitResults,
  });

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      if (stepIndex === 0) {
        setActiveStep(0);
        setUploadError(null);
      } else if (stepIndex === 1) {
        if (csvData.length > 0) {
          setActiveStep(1);
          setUploadError(null);
        }
      } else if (stepIndex === 2) {
        if (isValidated) {
          setActiveStep(2);
        }
      }
    },
    [csvData.length, isValidated]
  );

  const isStepClickable = useCallback(
    (stepIndex: number) => {
      if (stepIndex === 0) return true;
      if (stepIndex === 1) return csvData.length > 0;
      if (stepIndex === 2) return isValidated;
      return false;
    },
    [csvData.length, isValidated]
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Terminate employees in bulk
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Download the template, enter termination details for each row,
                then upload the file to review and submit.
              </Typography>
            </Box>

            {uploadError && (
              <Alert
                severity="error"
                onClose={() => setUploadError(null)}
                sx={{ mb: 3 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Upload Error
                </Typography>
                <Typography variant="body2">{uploadError}</Typography>
              </Alert>
            )}

            <Alert severity="info" variant="outlined" sx={{ mb: 3, py: 1.5 }}>
              Use the Excel template so column headers and validations match
              what the import expects.
            </Alert>

            <FileDropzone
              onFileSelect={handleFileSelect}
              isUploading={isUploading}
              title="Drag & drop or upload your file"
              description="Complete the template with one row per employee, then upload to continue"
              uploadButtonText="Upload completed template"
              templateButton={
                <LoadingButton
                  variant="contained"
                  size="medium"
                  loading={isDownloadingTemplate}
                  startIcon={<TableChartIcon />}
                  onClick={handleDownloadExcelTemplate}
                >
                  Excel template
                </LoadingButton>
              }
            />
          </Box>
        );

      case 1:
        return (
          <TerminationReviewTable
            rows={csvData}
            onBack={() => setActiveStep(0)}
            onValidate={() => handleValidate(csvData)}
            onNext={() => setActiveStep(2)}
            onDeleteRow={(index) => {
              setCsvData((prev) => prev.filter((_, i) => i !== index));
              setIsValidated(false);
            }}
            isValidating={isValidating}
            isValidated={isValidated}
          />
        );

      case 2:
        return (
          <TerminationSubmitStep
            rows={csvData}
            results={submitResults}
            isSubmitting={isSubmitting}
            submitProgress={submitProgress}
            onBack={() => setActiveStep(1)}
            onExecute={() => handleExecute(csvData)}
            onStartOver={() => {
              setCsvData([]);
              setIsValidated(false);
              setSubmitResults(null);
              setActiveStep(0);
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Helmet>
        <title>Bulk terminate employees | Armhr</title>
        <meta name="description" content="Bulk terminate employees" />
      </Helmet>

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography variant="h2">Bulk terminate employees</Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            Upload a spreadsheet to terminate multiple employees at once.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(paths.newHires)}
          sx={{ flexShrink: 0 }}
        >
          Back to Dashboard
        </Button>
      </Box>

      <WizardProgress
        steps={WIZARD_STEPS}
        activeStep={activeStep}
        onStepClick={handleStepClick}
        isStepClickable={isStepClickable}
      />

      <Box>{renderStepContent()}</Box>
    </Box>
  );
};

export default BulkTerminationPage;
